export interface PartsCoordinateEntry {
  text?: string;
  polygon?: number[][];
  [key: string]: unknown;
}

/**
 * Build a ref-number → coordinate map in a single pass.
 * Exact text match (e.g. "16") wins over partial match (e.g. "(16").
 */
export function buildCoordinateLookup(
  coordinates: PartsCoordinateEntry[]
): Map<string, PartsCoordinateEntry> {
  const exactMatches = new Map<string, PartsCoordinateEntry>();
  const partialMatches = new Map<string, PartsCoordinateEntry>();

  for (const coord of coordinates) {
    const rawText = (coord.text ?? '').trim();
    if (!rawText) continue;

    if (/^\d+$/.test(rawText) && !exactMatches.has(rawText)) {
      exactMatches.set(rawText, coord);
    }

    const embeddedRefs = rawText.match(/\d+/g) ?? [];
    for (const ref of embeddedRefs) {
      if (ref === rawText) continue;
      if (!partialMatches.has(ref)) {
        partialMatches.set(ref, coord);
      }
    }
  }

  const resolved = new Map<string, PartsCoordinateEntry>();
  const allRefs = new Set([...exactMatches.keys(), ...partialMatches.keys()]);

  for (const ref of allRefs) {
    const match = exactMatches.get(ref) ?? partialMatches.get(ref);
    if (match) {
      resolved.set(ref, { ...match, text: ref });
    }
  }

  return resolved;
}

export function getCoordinateForReference(
  lookup: Map<string, PartsCoordinateEntry>,
  referenceNumber: string
): PartsCoordinateEntry | undefined {
  const ref = referenceNumber.trim();
  if (!ref) return undefined;
  return lookup.get(ref);
}
